// R1b-β — Users tab body (rehomed from /admin/users into /admin/people-roles/users).
// R1b-β Step 2 — Extended with Invite User modal + status chips + clickable
// email → /admin/users/{id}. Original "Add user" (direct create) preserved
// for backwards compatibility.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "../../../components/EmptyState";
import StatusChip from "../../../components/StatusChip";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import PasswordInput from "../../../components/PasswordInput";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Trash2, Mail } from "lucide-react";

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role_key: "PROJECT_MANAGER", first_name: "", last_name: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const [form, setForm] = useState({ email: "", display_name: "", password: "", role_key: "PROJECT_MANAGER" });
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
    setUsers(u.data);
    setRoles(r.data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created (must rotate password on first login)");
      setOpen(false);
      setForm({ email: "", display_name: "", password: "", role_key: "PROJECT_MANAGER" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setInviteResult(null);
    try {
      const r = await api.post("/users/invite", inviteForm);
      setInviteResult(r.data);
      toast.success("Invite sent · outbox row queued for delivery");
      setInviteForm({ email: "", role_key: "PROJECT_MANAGER", first_name: "", last_name: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Suspend this user? They will not be able to log in until re-enabled.")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User suspended (soft-delete; record retained per DATA_SAFETY_RULES)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = users.filter((u) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (u.display_name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.role_key || "").toLowerCase().includes(q);
  });

  const statusForUser = (u) => {
    if (u.disabled) return { status: "error", label: "Suspended" };
    if (u.invited) return { status: "pending", label: "Invited" };
    if (u.must_rotate_password) return { status: "pending", label: "Awaiting rotation" };
    return { status: "connected", label: "Active" };
  };

  return (
    <div data-testid="users-tab-body">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="font-display font-semibold text-erf-dark text-lg">Users & Access</div>
          <div className="text-xs font-nav text-erf-dark/60 mt-0.5" data-testid="users-count">{users.length} user(s) in tenant · {filtered.length} shown</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="Search name / email / role…" value={query} onChange={(e) => setQuery(e.target.value)}
                 className="w-full sm:w-64 h-11" data-testid="users-search"/>

          <Dialog open={inviteOpen} onOpenChange={(v) => { setInviteOpen(v); if (!v) setInviteResult(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="users-invite-btn">
                <Mail className="w-4 h-4 mr-1"/> Invite user
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-erf-cream" data-testid="users-invite-dialog">
              <DialogHeader><DialogTitle className="font-display">Invite user</DialogTitle></DialogHeader>
              {inviteResult ? (
                <div className="space-y-3" data-testid="users-invite-result">
                  <div className="text-sm font-nav text-erf-dark" data-testid="users-invite-delivery">
                    Invite created. {inviteResult.delivery_label}. For security, the sign-up link is sent only by
                    email and is never shown here. It expires in 7 days.
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/admin/users/${inviteResult.user_id}`} onClick={() => setInviteOpen(false)}
                          data-testid="users-invite-view">
                      <Button variant="outline" className="border-erf-borderCream">Open user</Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={invite} className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase font-nav">Email</Label>
                    <Input type="email" required value={inviteForm.email}
                           onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                           className="mt-1 font-mono" data-testid="users-invite-email"/>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs uppercase font-nav">First name</Label>
                      <Input value={inviteForm.first_name}
                             onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                             className="mt-1" data-testid="users-invite-first-name"/>
                    </div>
                    <div>
                      <Label className="text-xs uppercase font-nav">Last name</Label>
                      <Input value={inviteForm.last_name}
                             onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                             className="mt-1" data-testid="users-invite-last-name"/>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase font-nav">Role</Label>
                    <Select value={inviteForm.role_key}
                            onValueChange={(v) => setInviteForm({ ...inviteForm, role_key: v })}>
                      <SelectTrigger className="mt-1" data-testid="users-invite-role"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {roles.filter(r => r.key !== "OWNER_SUPER_ADMIN").map(r => (
                          <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" disabled={busy}
                          className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                          data-testid="users-invite-submit">
                    {busy ? "Sending…" : "Send invite"}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-erf-borderCream" data-testid="users-add-btn">
                <Plus className="w-4 h-4 mr-1"/> Add user (direct)
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-erf-cream" data-testid="users-add-dialog">
              <DialogHeader><DialogTitle className="font-display">Add user (direct password)</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase font-nav">Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                         className="mt-1 font-mono" data-testid="users-add-email"/>
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">Display name</Label>
                  <Input required value={form.display_name} onChange={(e) => setForm({...form, display_name: e.target.value})}
                         className="mt-1" data-testid="users-add-name"/>
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">Temporary password</Label>
                  <PasswordInput required minLength={12} value={form.password}
                                 onChange={(e) => setForm({...form, password: e.target.value})}
                                 className="mt-1 font-mono" testId="users-add-password-visible" autoComplete="new-password"/>
                  <div className="text-[11px] text-erf-dark/60 mt-1 font-nav">User will be forced to rotate on first login.</div>
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">Role</Label>
                  <Select value={form.role_key} onValueChange={(v) => setForm({...form, role_key: v})}>
                    <SelectTrigger className="mt-1" data-testid="users-add-role"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {roles.filter(r => r.key !== "OWNER_SUPER_ADMIN").map(r => (
                        <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={busy}
                        className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="users-add-submit">
                  {busy ? "Creating…" : "Create user"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
        <table className="w-full erf-table erf-stack-sm" data-testid="users-table">
          <thead className="bg-erf-creamSecondary">
            <tr>
              <th className="text-left px-3 py-2">Display name</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Test</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-0">
                <EmptyState icon={UsersIcon} title="No users match" subtitle="Adjust the search or invite a new user."/>
              </td></tr>
            )}
            {filtered.map((u) => {
              const s = statusForUser(u);
              return (
              <tr key={u.id} className="border-t border-erf-borderCream/60" data-testid={`user-row-${u.id}`}>
                <td data-label="Display name" className="px-3 py-2 font-nav font-semibold">{u.display_name || "—"}</td>
                <td data-label="Email" className="px-3 py-2 mono">
                  <Link to={`/admin/users/${u.id}`} className="inline-flex items-center min-h-[44px] break-all text-erf-dark hover:text-erf-gold hover:underline"
                        data-testid={`user-link-${u.id}`}>
                    {u.email}
                  </Link>
                </td>
                <td data-label="Role" className="px-3 py-2">
                  <span className="font-mono text-[11px] px-1.5 py-0.5 rounded-sm border border-erf-borderCream bg-erf-creamSecondary">
                    {u.role_key}
                  </span>
                </td>
                <td data-label="Status" className="px-3 py-2">
                  <StatusChip status={s.status} label={s.label}/>
                </td>
                <td data-label="Test" className="px-3 py-2">
                  {u.is_test_account && <StatusChip status="test" testId={`user-test-chip-${u.id}`}/>}
                </td>
                <td data-label="Actions" className="px-3 py-2 text-right">
                  {u.role_key !== "OWNER_SUPER_ADMIN" && (
                    <Button size="sm" variant="ghost" className="text-red-700 h-8" onClick={() => remove(u.id)}
                            data-testid={`user-delete-${u.id}`} title="Suspend user (soft-delete)">
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
